/**
 * autosave_controller.js — saves a settings form as it is edited (Stimulus).
 *
 * Every settings handler on the server reads the WHOLE form (an unticked
 * checkbox is simply absent, and absence means off), so a save always posts
 * the complete form, never the one field that changed.
 *
 * Modes (data-autosave-mode-value):
 *   json    (default) POST the form to its action through window.api; the action
 *           answers {success, message} JSON. Toggles, selects and radios save on
 *           change. Typed text saves once typing pauses, and again on blur.
 *           Number inputs save on change only, so a half-typed value ("10" on
 *           the way to "1000") is never written: starting numbers reset entity
 *           sequences.
 *   submit  a native submit on change, for file pickers and toggles whose
 *           endpoint redirects and whose result needs the page re-rendered.
 *   manual  never saves. Tracks unsaved edits for a form that keeps its Save
 *           button because a change is consequential (region and tax, the
 *           public profile's web address).
 *
 * Saving is single-flight: an edit made while a save is in flight queues one
 * more save of the then-current form, so the last write carries the latest
 * values. A save still waiting on its typing pause when the page goes away is
 * sent with navigator.sendBeacon; the form's csrf_token field rides in the body.
 *
 * The controller keeps a signature of the form as the server last accepted it
 * (as rendered, then after each save). An edit that leaves the form equal to it,
 * such as the blur that follows an already-saved typing pause, or typing then
 * deleting, posts nothing and clears the marker, and so is not unsaved work.
 *
 * A form the browser considers invalid is not posted. While the invalid field
 * still has focus the marker only says unsaved; once focus leaves it, the field
 * is highlighted with the browser's reason under it until it is valid, and the
 * marker names it with a Show button that scrolls to it. A web address typed
 * without https:// gets it on change, rather than blocking the form.
 *
 * Unsaved work (./_unsaved_work.js prompts before leaving with it, and marks the
 * tab holding it): any edit to a manual form; for a json form only an edit that
 * cannot be sent on the way out, because its save failed or the form is invalid.
 *
 * Markers (./_save_status.js) sit in the header of the edited section. A manual
 * form also shows one beside its Save button (the status target), so the change
 * is flagged both where it was made and where it gets saved.
 *
 * A container inside the form may dispatch a bubbling `change` to announce a
 * structural edit with no field of its own (business_licences removing a row).
 *
 * Targets: status  (manual mode) the marker slot beside the Save button.
 * Events:  autosave:saved  {detail: {formData}} after each successful json save.
 */

import { Controller } from "@hotwired/stimulus";
import { clearSaveStatus, saveStatusFor, showSaveStatus } from "./_save_status.js";
import { guardUnsavedWork, releaseUnsavedWork, unsavedWorkChanged } from "./_unsaved_work.js";

const TYPING_PAUSE_MS = 1000;
const TYPED_TYPES = new Set(['text', 'email', 'tel', 'url', 'search', 'textarea']);

export default class extends Controller {
    static targets = ['status'];
    static values = { mode: { type: String, default: 'json' } };

    connect() {
        this._pending = new Set();   // markers of sections edited since the last save began
        this._saving = null;
        this._queued = false;
        this._timer = null;
        this._savedSignature = this.modeValue === 'submit' ? null : this._signature();
        this._onInput = (event) => this._edited(event.target, false);
        this._onChange = (event) => this._edited(event.target, true);
        this._onSubmit = (event) => this._submitted(event);
        this._onPageHide = () => this._flush();
        this.element.addEventListener('input', this._onInput);
        this.element.addEventListener('change', this._onChange);
        this.element.addEventListener('submit', this._onSubmit);
        window.addEventListener('pagehide', this._onPageHide);
        if (this.modeValue !== 'submit') guardUnsavedWork(this.element, () => this._hasUnsavedWork());
    }

    disconnect() {
        this._flush();
        this.element.removeEventListener('input', this._onInput);
        this.element.removeEventListener('change', this._onChange);
        this.element.removeEventListener('submit', this._onSubmit);
        window.removeEventListener('pagehide', this._onPageHide);
        releaseUnsavedWork(this.element);
    }

    /** Post the whole form now. Also what a failed marker's Retry calls. */
    save() {
        clearTimeout(this._timer);
        this._timer = null;
        if (this._saving) {
            this._queued = true;
        } else if (this._readyToSend()) {
            this._send();
        }
        unsavedWorkChanged();
    }

    _send() {
        const formData = new FormData(this.element);
        const signature = new URLSearchParams(formData).toString();
        if (signature === this._savedSignature) {
            this._settle();
            return;
        }
        const markers = new Set(this._pending);
        this._pending.clear();
        markers.forEach((marker) => showSaveStatus(marker, 'saving'));
        this._saving = window.api(this.element.action, { method: 'POST', body: formData, notifyError: false })
            .then((data) => this._succeeded(data, markers, formData, signature))
            .catch((error) => this._failed(error.message, markers))
            .finally(() => {
                this._saving = null;
                if (this._queued) {
                    this._queued = false;
                    this.save();
                }
            });
    }

    _edited(field, committed) {
        if (!this._tracks(field)) return;
        if (committed) addUrlScheme(field);
        if (field.checkValidity && field.checkValidity()) clearInvalid(field);
        const markers = this._markersFor(field);

        if (this.modeValue === 'submit') {
            if (!committed) return;
            markers.forEach((marker) => showSaveStatus(marker, 'saving'));
            this.element.requestSubmit();
            return;
        }

        // While a save is in flight the server's state is about to move, so an
        // edit back to the old signature still has to be saved.
        if (!this._saving && this._signature() === this._savedSignature) {
            this._settle();
        } else {
            markers.forEach((marker) => {
                this._pending.add(marker);
                showSaveStatus(marker, 'dirty');
            });
            if (this.modeValue === 'json') this._schedule(field, committed);
        }
        unsavedWorkChanged();
    }

    _schedule(field, committed) {
        if (committed) {
            this.save();
        } else if (TYPED_TYPES.has(field.type)) {
            clearTimeout(this._timer);
            this._timer = setTimeout(() => this.save(), TYPING_PAUSE_MS);
        }
    }

    _tracks(field) {
        if (!('form' in field)) return true;
        if (!field.name) return false;
        return this.modeValue === 'submit' || field.type !== 'file';
    }

    _markersFor(field) {
        const markers = [saveStatusFor(field)];
        if (this.hasStatusTarget) markers.push(saveStatusFor(field, this.statusTarget));
        return markers.filter(Boolean);
    }

    _hasUnsavedWork() {
        if (!this._pending.size) return false;
        if (this.modeValue === 'manual') return true;
        const failed = Array.from(this._pending).some((marker) => marker.dataset.state === 'failed');
        return failed || !this.element.checkValidity();
    }

    _readyToSend() {
        const invalid = Array.from(this.element.elements)
            .filter((el) => el.willValidate && !el.checkValidity());
        if (!invalid.length) return true;

        const settled = invalid.filter((el) => el !== document.activeElement);
        settled.forEach(markInvalid);
        if (settled.length) {
            const first = settled[0];
            this._markersFor(first).forEach((marker) => {
                this._pending.add(marker);
                showSaveStatus(marker, 'failed', {
                    detail: 'Not saved: check ' + fieldName(first),
                    action: { label: 'Show', run: () => showField(first) },
                });
            });
        }
        return false;
    }

    _signature() {
        return new URLSearchParams(new FormData(this.element)).toString();
    }

    // Nothing differs from what the server holds: drop any pending save and marker.
    _settle() {
        clearTimeout(this._timer);
        this._timer = null;
        this._pending.forEach((marker) => clearSaveStatus(marker));
        this._pending.clear();
    }

    _succeeded(data, markers, formData, signature) {
        if (!data || !data.success) {
            this._failed(data && data.message, markers);
            return;
        }
        this._savedSignature = signature;
        markers.forEach((marker) => {
            if (!this._pending.has(marker)) showSaveStatus(marker, 'saved');
        });
        this.dispatch('saved', { detail: { formData } });
        unsavedWorkChanged();
    }

    _failed(message, markers) {
        markers.forEach((marker) => {
            this._pending.add(marker);
            showSaveStatus(marker, 'failed', { retry: () => this.save() });
        });
        unsavedWorkChanged();
        if (typeof window.showNotification === 'function') {
            window.showNotification(message || 'Could not save your settings', 'error');
        }
    }

    _submitted(event) {
        if (this.modeValue === 'json') {
            event.preventDefault();
            this.save();
        } else if (this.modeValue === 'manual') {
            // The native POST-redirect is the save: no longer unsaved, so the
            // redirect's visit is not stopped by the leave prompt.
            this._pending.forEach((marker) => showSaveStatus(marker, 'saving'));
            this._pending.clear();
            unsavedWorkChanged();
        }
    }

    // A typing pause cut short by leaving the page would otherwise lose the edit.
    _flush() {
        if (this.modeValue !== 'json' || (!this._timer && !this._queued)) return;
        clearTimeout(this._timer);
        this._timer = null;
        this._queued = false;
        if (this.element.checkValidity() && this._signature() !== this._savedSignature) {
            navigator.sendBeacon(this.element.action, new FormData(this.element));
        }
    }
}

// A web address typed without its scheme ("smithcarpentry.com.au") is what
// people type; a url input refuses it, so supply the https:// it means.
export function addUrlScheme(field) {
    const value = field.type === 'url' ? field.value.trim() : '';
    if (value && !/^[a-z][a-z0-9+.-]*:/i.test(value)) field.value = 'https://' + value;
}

// The highlight and the browser's reason sit under the field, and stay until
// the field is valid, wherever the section's marker is scrolled to.
export function markInvalid(field) {
    field.classList.add('is-invalid');
    let feedback = field.nextElementSibling;
    if (!feedback || !feedback.hasAttribute('data-autosave-feedback')) {
        feedback = document.createElement('div');
        feedback.className = 'invalid-feedback d-block';
        feedback.setAttribute('data-autosave-feedback', '');
        field.insertAdjacentElement('afterend', feedback);
    }
    feedback.textContent = field.validationMessage;
}

export function clearInvalid(field) {
    field.classList.remove('is-invalid');
    const feedback = field.nextElementSibling;
    if (feedback && feedback.hasAttribute('data-autosave-feedback')) feedback.remove();
}

function showField(field) {
    field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    field.focus({ preventScroll: true });
}

function fieldName(field) {
    const label = field.labels && field.labels[0];
    const text = label ? label.textContent : field.getAttribute('aria-label');
    return (text || 'the highlighted field').replace('*', '').trim();
}
